import { type Repub } from '@cx-modules/types';

import { REPUB_CONFIG } from '~/repub-config';

const baseStyles = (backgroundColor: string = 'var(--ogm-timberwolves)') => `
            body,
            input {
                font-family: "Public Sans", sans-serif;
                background-color: ${backgroundColor};
                display: flex;
                max-width: 1250px;
                justify-content: center;
                align-items: center;
                min-width: 100%;
                height: 100vh;
            }

            p {
                display: block;
                margin-block-start: 1em;
                margin-block-end: 1em;
                margin-inline-start: 0px;
                margin-inline-end: 0px;
                font-family: "Public Sans", sans-serif;
            }

            code,
            kbd,
            pre {
                font-family: "Public Sans", sans-serif;
            }`;

const stylesWithTranslations = (backgroundColor: string = 'var(--ogm-timberwolves)') => `
            ${baseStyles(backgroundColor)}

            .mobileImage {
                display: none;
            }

            .desktopImage {
                margin-right: 20px;
            }

            .textSection {
                padding: 20px;
            }

            .homepage {
                background-color: var(--ogm-seahawks);
                color: var(--ogm-timberwolves);
                border: none;
            }

            .homepage:hover {
                color: var(--ogm-white);
                background-color: var(--ogm-bucks);
            }

            a {
                display: inline-block;
                border: 1px solid white;
                border-radius: 4px;
                background-color: var(--ogm-timberwolves);
                color: var(--ogm-white);
                padding: 10px;
                margin-bottom: 10px;
                margin-right: 8px;
                font-family: "Public Sans", sans-serif;
                font-weight: 700;
                text-decoration: none;
            }

            a:hover {
                background-color: var(--ogm-white);
                color: var(--ogm-timberwolves);
            }

            .buttonSection {
                padding-top: 10px;
            }

            .text {
                font-size: 18px;
                color: var(--ogm-white);
            }

            .oops {
                padding-top: 32px;
                font-family: "futura-pt-condensed", sans-serif;
                color: var(--ogm-seahawks);
                font-size: 72px;
            }

            @media only screen and (max-width: 1024px) {
                body {
                    text-align: center;
                    margin: 0px;
                    padding: 0px;
                    width: 100vw;
                }

                .textSection {
                    height: 100%;
                    width: 100%;
                    padding: 12px;
                }

                .text {
                    font-size: 18px;
                }

                .desktopImage {
                    display: none;
                }

                .mobileImage {
                    display: flex;
                    height: 350px;
                    width: 350px;
                    margin: 20px auto;
                }

                .logo {
                    height: 30px;
                }
            }

            @media only screen and (max-width: 600px) {
                .oops {
                    font-size: 48px;
                }

                .text {
                    font-size: 16px;
                }

                .logo {
                    height: 20px;
                }

                .mobileImage {
                    height: 200px;
                    width: 200px;
                }
            }
        `;

const stylesWithoutTranslations = (backgroundColor: string = 'var(--ogm-timberwolves)') => `
            ${baseStyles(backgroundColor)}
            .errorGrid {
              display: grid;
              grid-template-areas:
                "mainLogo"
                "ocLogo"
                "errorStatusText"
                "homeIcon";
              padding: 24px;
            }

            .mainLogo {
              grid-area: mainLogo;
              color: white;
              padding: 16px;
              display: flex;
              justify-content: center;
              align-items: center;
            }

            .ocLogo {
              grid-area: ocLogo;
              color: white;
              padding: 18px 16px;
              text-align: center;
            }

            .ocLogoMobile {
                display: inline-block;
            }

            .ocLogoDesktop {
                display: none;
            }

            .errorStatusText {
              grid-area: errorStatusText;
              color: #00E889;
              font-family: "futura-pt-condensed", sans-serif;
              font-size: 112px;
              font-weight: bold;
              line-height: 1;
              text-transform: uppercase;
              display: grid;
              place-items: center;
            }

            .homeIcon {
              grid-area: homeIcon;
              color: #23395d;
              padding: 16px;
              text-align: center;
            }

            .homeIcon a {
                border: none;
                margin: 0;
            }

            .homeIcon a:hover {
                border: none;
                background-color: transparent;
            }

            @media (min-width: 1025px) {
              .errorGrid {
                grid-template-areas:
                  "mainLogo ocLogo"
                  "errorStatusText ocLogo"
                  "homeIcon ocLogo";
                grid-template-columns: auto 1fr;
                grid-template-rows: 1fr auto 1fr;
                margin: 0 auto;
              }

              .mainLogo {
                align-items: flex-end;
              }

              .ocLogo {
                padding: 16px 16px 16px 104px;
              }

                .ocLogoMobile {
                    display: none;
                }

                .ocLogoDesktop {
                    display: inline-block;
                }

              .errorStatusText {
                font-size: 200px;
              }
            }

            @media only screen and (max-width: 1024px) {
                body {
                    text-align: center;
                    margin: 0px;
                    padding: 0px;
                    width: 100vw;
                }

                .textSection {
                    height: 100%;
                    width: 100%;
                    padding: 12px;
                }

                .text {
                    font-size: 18px;
                }

                .mobileImage {
                    display: flex;
                    height: 350px;
                    width: 350px;
                    margin: 20px auto;
                }

                .logo {
                    height: 30px;
                }
            }

            @media only screen and (max-width: 600px) {
                .oops {
                    font-size: 48px;
                }

                .text {
                    font-size: 16px;
                }

                .logo {
                    height: 20px;
                }

                .mobileImage {
                    height: 200px;
                    width: 200px;
                }
            }
        `;

export function ErrorPageStyles({ repub, translationReady }: { repub: Repub; translationReady: boolean }) {
  // Get background color from REPUB_CONFIG, fallback to default
  const backgroundColor = REPUB_CONFIG[repub]?.plus?.errorPage?.backgroundColor ?? 'var(--ogm-timberwolves)';
  const css = translationReady ? stylesWithTranslations(backgroundColor) : stylesWithoutTranslations(backgroundColor);
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}
